import { TaskStatus } from '@/components/CreateSheet/constants';
import type { DataTableSortState } from '@/components/DataTable/DataTable';
import {
  DUE_SOON_MAX_WINDOW_MS,
  OPEN_TASK_STATUSES,
} from '@/components/TaskExpiringLabel/taskExpiration';
import type { TaskFilters } from '@/store/tasks/task-filters';
import type { TasksSortField, TaskStatusType } from '@/store/tasks/tasks.dto';
import type { TasksListFilter } from '@/store/tasks/tasks.queries';
import {
  type MaintainPeriod,
  makePeriod,
  periodRange,
  readPeriodParams,
  writePeriodParams,
} from '@pages/Maintain/period';

/** `?overdue=1` / `?dueSoon=1` on /maintain/schedule land on a pre-filtered list. */
export const OVERDUE_PARAM = 'overdue';
export const DUE_SOON_PARAM = 'dueSoon';
export const UPCOMING_PARAM = 'upcoming';
export const DONE_PARAM = 'done';

export type ScheduleFilterValue = 'overdue' | 'dueSoon' | 'upcoming' | 'done';

/** The part of a tasks request a schedule filter decides. */
type ScheduleCut = Pick<TasksListFilter, 'status' | 'from' | 'to'>;

/** The statuses whose deadline still means something — the three due buckets' floor. */
const OPEN: TaskStatusType[] = OPEN_TASK_STATUSES as TaskStatusType[];

/** Where "due soon" stops and "upcoming" begins, as a date the service can filter on. */
const dueSoonHorizon = (now: string) =>
  new Date(new Date(now).getTime() + DUE_SOON_MAX_WINDOW_MS).toISOString();

type ScheduleFilterOption = {
  value: ScheduleFilterValue;
  label: string;
  /** `?<param>=1`, so a drill-down link and the popover share one source of truth. */
  param: string;
  /** What the selection means in TaskFilters terms, over the defaults. */
  patch: Partial<TaskFilters>;
  matches: (filters: TaskFilters) => boolean;
  /**
   * The same selection as `GET /tasks` query parameters — the list is paged by the
   * service. Due soon is the one exception: its cut is a superset, narrowed in
   * `schedule-list.queries.ts`.
   *
   * Activity reads a `from`/`to` *pair* as "ends after `from`, starts before `to`", and
   * either bound alone as `start >= from` / `end <= to`
   * (`internal/pkg/database/task.go:703`). Those three readings partition the open jobs
   * with nothing falling between them and nothing counted twice:
   * deadline passed | still running or starting within the day | starting later.
   */
  cut: (now: string) => ScheduleCut;
};

/**
 * The schedule's one flat, single-select filter set. Overdue / due soon / upcoming are
 * buckets over an open job's dates, not statuses, while Done is the completed status
 * itself.
 */
export const SCHEDULE_FILTERS: ScheduleFilterOption[] = [
  {
    value: 'overdue',
    label: 'Overdue',
    param: OVERDUE_PARAM,
    patch: { overdue: true },
    matches: (filters) => !!filters.overdue,
    // Deadline already gone by.
    cut: (now) => ({ status: OPEN, to: now }),
  },
  {
    value: 'dueSoon',
    label: 'Due Soon',
    param: DUE_SOON_PARAM,
    patch: { dueSoon: true },
    matches: (filters) => !!filters.dueSoon,
    // A superset — deadline still ahead, and the job is already running or starts within
    // the day. The service cannot state "deadline within the day", so this cut is the one
    // narrowed in the browser, by `isTaskDueSoon`: see `schedule-list.queries.ts`.
    cut: (now) => ({ status: OPEN, from: now, to: dueSoonHorizon(now) }),
  },
  {
    value: 'upcoming',
    label: 'Upcoming',
    param: UPCOMING_PARAM,
    patch: { upcoming: true },
    matches: (filters) => !!filters.upcoming,
    // Not started yet and not starting within the day — the rest of the open jobs.
    cut: (now) => ({ status: OPEN, from: dueSoonHorizon(now) }),
  },
  {
    value: 'done',
    label: 'Done',
    param: DONE_PARAM,
    patch: { status: [TaskStatus.COMPLETED] },
    matches: (filters) => filters.status.length === 1 && filters.status[0] === TaskStatus.COMPLETED,
    cut: () => ({ status: [TaskStatus.COMPLETED] as TaskStatusType[] }),
  },
];

// A factory, not a shared constant: the pages refetch on filter identity, so handing
// back the same object twice would swallow a repeated "Clean filter".
export const defaultTaskFilters = (): TaskFilters => ({
  startDate: '',
  endDate: '',
  type: '',
  status: [],
  responsible: '',
  equipment: '',
  overdue: false,
  dueSoon: false,
  upcoming: false,
  year: '',
  month: '',
});

export const emptyFilterValue = (key: keyof TaskFilters) => defaultTaskFilters()[key];

/** The whole filter state for one selection — nothing else on the schedule is filterable. */
export const scheduleFilters = (value?: ScheduleFilterValue): TaskFilters => ({
  ...defaultTaskFilters(),
  ...SCHEDULE_FILTERS.find((option) => option.value === value)?.patch,
});

/** Which option the current filters read as, so the popover reflects an arriving link. */
export const scheduleFilterOf = (filters: TaskFilters): ScheduleFilterValue | undefined =>
  SCHEDULE_FILTERS.find((option) => option.matches(filters))?.value;

/**
 * The period is a second, independent field: it narrows whichever Show option is picked
 * by deadline rather than replacing it, so switching one leaves the other alone.
 */
export const withScheduleFilter = (
  filters: TaskFilters,
  value?: ScheduleFilterValue,
): TaskFilters => ({
  ...scheduleFilters(value),
  year: filters.year,
  month: filters.month,
});

/** The schedule's period, as the shared Maintain picker reads it. */
export const periodOf = (filters: TaskFilters): MaintainPeriod =>
  makePeriod(filters.year || undefined, filters.month || undefined);

export const withPeriod = (filters: TaskFilters, period: MaintainPeriod): TaskFilters => ({
  ...filters,
  ...period,
});

export const readScheduleParams = (params: URLSearchParams): TaskFilters =>
  withPeriod(
    scheduleFilters(SCHEDULE_FILTERS.find((option) => params.get(option.param) === '1')?.value),
    readPeriodParams(params),
  );

const setOrDelete = (params: URLSearchParams, key: string, value?: string) => {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
};

/**
 * The cut, as params. A default states nothing — no chip means no filter param, so an
 * untouched schedule has a clean URL. Which page is open is not written here: it is
 * `usePageParam`'s, shared with every other server-paged list.
 */
export const writeScheduleParams = (params: URLSearchParams, filters: TaskFilters) => {
  const next = new URLSearchParams(params);
  const active = scheduleFilterOf(filters);
  SCHEDULE_FILTERS.forEach(({ value, param }) => {
    setOrDelete(next, param, value === active ? '1' : undefined);
  });
  writePeriodParams(next, periodOf(filters));
  return next;
};

/**
 * The schedule's filter as activity's, in one place.
 *
 * Every field is filtered by the service, so the rows the page renders are the rows it
 * asked for — no client-side second pass, which is what a paged list needs: a page
 * narrowed after the fact would hold fewer rows than it asked for and disagree with the
 * `count` the footer prints. (Due soon avoids that by fetching its whole superset and
 * paging it itself — `schedule-list.queries.ts`.)
 *
 * `equipment_id: 'all'` is the maintenance cut itself — activity reads it as
 * `equipment IS NOT NULL`, so plain tasks never travel and the page has nothing to
 * guard against.
 */
export const toScheduleFilter = (
  filters: TaskFilters,
  { limit, now, sort }: { limit: number; now: string; sort?: DataTableSortState | null },
): TasksListFilter => ({
  limit,
  equipment_id: filters.equipment || 'all',
  ...SCHEDULE_FILTERS.find((option) => option.matches(filters))?.cut(now),
  ...periodRange(periodOf(filters)),
  ...(sort && { sort: sort.field as TasksSortField, order: sort.direction }),
});

/**
 * The schedule table's sortable columns — the ones activity can order by as shown.
 * Interval lives on the generator. Est. cost falls back to the invoice amount from wallet,
 * which activity can't see — sorting by `cost` would put invoice-only rows with the blanks,
 * so that header stays plain.
 */
export const SCHEDULE_SORT_FIELDS = [
  'title',
  'equipment',
  'due',
  'responsible',
  'status',
] as const satisfies TasksSortField[];
